/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useCallback, useEffect, useRef } from 'react';

export function useTrapFocus(autofocus?: boolean) {
  const ref = useRef<HTMLDivElement>(null);

  const getFocusableElements = (element: HTMLDivElement): NodeListOf<HTMLElement> => {
    return element.querySelectorAll('a[href], textarea, input, button, select');
  };

  const handler = useCallback((event: KeyboardEvent): void => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const focusableElements = getFocusableElements(element);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const isTabPressed = event.key === 'Tab';

    if (!isTabPressed) {
      return;
    }

    if (event.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement?.focus();
        event.preventDefault();
      }
    } else if (document.activeElement === lastFocusableElement) {
      firstFocusableElement?.focus();
      event.preventDefault();
    }
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (element) {
      if (autofocus) {
        const focusableElements = getFocusableElements(element);
        focusableElements[0]?.focus();
      } else {
        element.focus();
      }

      element.addEventListener('keydown', handler);

      return () => {
        element.removeEventListener('keydown', handler);
      };
    }
  }, [handler]);

  return ref;
}
